import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './CredentialsSettingsPage.module.css';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../utils/apiError';

const CredentialsSettingsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('credentials');
  const { t: tApi } = useTranslation('apiErrors');
  const [activeTab, setActiveTab] = useState('email');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    current_email: '',
    email: '',
    email_confirmation: '',
    current_password_email: '',
    password: '',
    password_confirmation: '',
    current_password_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadCurrentUser = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/user`);
        const user = res?.data || {};
        if (!mounted) return;

        setForm((prev) => ({
          ...prev,
          name: user.name || '',
          phone: user.phone || '',
          current_email: user.email || '',
        }));
        setUserRole(user.role || '');
      } catch {
        // 読み込み失敗時は既存入力のまま継続する
      }
    };

    loadCurrentUser();
    return () => {
      mounted = false;
    };
  }, []);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError('');
    setSuccess('');
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const finishWithRelogin = (message) => {
    setSuccess(message || t('successRelogin'));
    setTimeout(() => navigate('/login', { replace: true }), 1200);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const nextEmail = form.email.trim();
      const confirmEmail = form.email_confirmation.trim();
      if (!nextEmail) {
        setError(t('errorEmailRequired'));
        return;
      }
      if (nextEmail !== confirmEmail) {
        setError(t('errorEmailMismatch'));
        return;
      }

      const payload = {
        email: nextEmail,
        current_password: form.current_password_email,
      };

      const res = await axios.put(`${process.env.REACT_APP_API_URL}/user/credentials`, payload);
      finishWithRelogin(res?.data?.message);
    } catch (err) {
      setError(getApiErrorMessage(err, tApi));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!form.password) {
        setError(t('errorPasswordRequired'));
        return;
      }

      if (form.password.length < 8) {
        setError(t('errorPasswordLength'));
        return;
      }

      const payload = {
        password: form.password,
        password_confirmation: form.password_confirmation,
        current_password: form.current_password_password,
      };

      const res = await axios.put(`${process.env.REACT_APP_API_URL}/user/credentials`, payload);
      finishWithRelogin(res?.data?.message);
    } catch (err) {
      setError(getApiErrorMessage(err, tApi));
    } finally {
      setLoading(false);
    }
  };

  const handleOtherSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const nextName = form.name.trim();
      if (!nextName) {
        setError(t('errorNameRequired'));
        return;
      }

      if (!form.current_email) {
        setError(t('errorProfileLoad'));
        return;
      }

      const payload = {
        name: nextName,
        email: form.current_email,
      };
      if (userRole !== 'buyer') {
        payload.phone = form.phone.trim() || null;
      }

      const res = await axios.put(`${process.env.REACT_APP_API_URL}/user/profile`, payload);
      setSuccess(res?.data?.message || t('successOther'));
    } catch (err) {
      setError(getApiErrorMessage(err, tApi));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t('title')}</h1>
      <p className={styles.desc}>{t('desc')}</p>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'email' ? styles.tabActive : ''}`}
          type="button"
          onClick={() => switchTab('email')}
        >
          {t('tabEmail')}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'password' ? styles.tabActive : ''}`}
          type="button"
          onClick={() => switchTab('password')}
        >
          {t('tabPassword')}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'other' ? styles.tabActive : ''}`}
          type="button"
          onClick={() => switchTab('other')}
        >
          {t('tabOther')}
        </button>
      </div>

      {activeTab === 'email' && (
        <form className={styles.form} onSubmit={handleEmailSubmit}>
          <input
            type="email"
            name="username"
            value={form.current_email}
            autoComplete="username"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className={styles.hiddenAutofillField}
          />

          <label className={styles.label}>{t('labelNewEmail')}</label>
          <input
            className={styles.input}
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            autoComplete="email"
            placeholder="new-email@example.com"
          />

          <label className={styles.label}>{t('labelNewEmailConfirm')}</label>
          <input
            className={styles.input}
            type="email"
            name="email_confirmation"
            value={form.email_confirmation}
            onChange={handleChange}
            required
            autoComplete="email"
            placeholder="new-email@example.com"
          />

          <label className={styles.label}>{t('labelCurrentPassword')}</label>
          <input
            className={styles.input}
            type="password"
            name="current_password_email"
            value={form.current_password_email}
            onChange={handleChange}
            required
            autoComplete="current-password"
            placeholder={t('placeholderCurrentPassword')}
          />

          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? t('btnUpdate') : t('btnUpdateEmail')}
          </button>
        </form>
      )}

      {activeTab === 'password' && (
        <form className={styles.form} onSubmit={handlePasswordSubmit}>
          <input
            type="email"
            name="username"
            value={form.current_email}
            autoComplete="username"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className={styles.hiddenAutofillField}
          />

          <label className={styles.label}>{t('labelNewPassword')}</label>
          <input
            className={styles.input}
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            required
            autoComplete="new-password"
            placeholder={t('placeholder8chars')}
          />

          <label className={styles.label}>{t('labelNewPasswordConfirm')}</label>
          <input
            className={styles.input}
            type="password"
            name="password_confirmation"
            value={form.password_confirmation}
            onChange={handleChange}
            required
            autoComplete="new-password"
            placeholder={t('placeholderConfirm')}
          />

          <label className={styles.label}>{t('labelCurrentPassword')}</label>
          <input
            className={styles.input}
            type="password"
            name="current_password_password"
            value={form.current_password_password}
            onChange={handleChange}
            required
            autoComplete="current-password"
            placeholder={t('placeholderCurrentPassword')}
          />

          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? t('btnUpdate') : t('btnUpdatePassword')}
          </button>
        </form>
      )}

      {activeTab === 'other' && (
        <form className={styles.form} onSubmit={handleOtherSubmit}>
          <label className={styles.label}>{userRole === 'buyer' ? t('labelName') : t('labelNameRep')}</label>
          <input
            className={styles.input}
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            placeholder="山田 太郎"
          />

          {userRole !== 'buyer' && (
            <>
              <label className={styles.label}>{t('labelPhone')}</label>
              <input
                className={styles.input}
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="09012345678"
              />
            </>
          )}

          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? t('btnUpdate') : t('btnUpdateOther')}
          </button>
        </form>
      )}
    </div>
  );
};

export default CredentialsSettingsPage;
